import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, LogOut, Settings as SettingsIcon, Sparkles, MessageSquare, MessageCircle, Send, Swords } from 'lucide-react';

import { env } from '@/env';
import { useAppSelector } from '@/store/hooks';
import { useMyTournamentsQuery } from '@/services/endpoints/tournaments';
import { useGetMeQuery } from '@/services/endpoints/auth';
import { useGetMyAvatarsQuery } from '@/services/avatarApi';
import {
  useListFollowersQuery,
  useListFollowingQuery,
  useListFriendsQuery,
} from '@/services/socialApi';
import { useGetMyLinkQuery } from '@/services/qduelsApi';
import { isNotLinkedError } from '@/services/qduelsErrors';
import { useGetWalletsQuery } from '@/services/qbankApi';
import { QBANK_DEFAULT_COIN, type Wallet } from '@/types/qbank';
import { useLogtoAuth } from '@/hooks/useLogtoAuth';
import { Loader } from '@/components/common/Loader';
import { StatusBadge } from '@/components/tournaments/StatusBadge';
import { formatDateShort } from '@/lib/format';
import { UserHeaderBanner } from '@/components/banner/UserHeaderBanner';
import { CustomizeSheet } from '@/components/banner/CustomizeSheet';

export function Profile() {
  const { t } = useTranslation('general');
  const { signOut } = useLogtoAuth();
  const meFromStore = useAppSelector((s) => s.auth.user);
  const meQuery = useGetMeQuery(undefined, { skip: !!meFromStore });
  const me = meFromStore ?? meQuery.data ?? null;
  const [customizeOpen, setCustomizeOpen] = useState(false);

  const myTournaments = useMyTournamentsQuery({ limit: 5 });
  // Social counts (qsocials). Cheap queries — accepted+pending lists, no
  // per-user enrichment. Each list is also paged so this only loads what
  // the user actually has.
  const friendsQuery = useListFriendsQuery();
  const followingQuery = useListFollowingQuery();
  const followersQuery = useListFollowersQuery();
  const friendsCount = friendsQuery.data?.length ?? 0;
  const followingCount = followingQuery.data?.length ?? 0;
  const followersCount = followersQuery.data?.length ?? 0;
  // Clash Royale duel-account link status (qduels). 404 → not linked. We only
  // surface the linked tag here; the full link/relink flow lives at /duels/link.
  const crLinkQuery = useGetMyLinkQuery();
  const crNotLinked = isNotLinkedError(crLinkQuery.error);
  const crLinkedTag = crLinkQuery.data?.playerTag ?? null;
  // Skip the query when avatars are disabled (PROD) — no request, no badge,
  // no section. Section block below is also gated on the same flag.
  const myAvatars = useGetMyAvatarsQuery(
    { page: 1, limit: 6 },
    { skip: !env.featureAvatarsEnabled },
  );
  const avatarItems = myAvatars.data?.items ?? [];
  // QBank spike: read-only wallet balance + EXP level. Skipped entirely when the
  // flag is off (PROD) — no request, no card.
  const qbankWallets = useGetWalletsQuery(undefined, { skip: !env.featureQbankEnabled });
  const wallets = qbankWallets.data ?? [];
  // Entitled = the read succeeded (a 401 means no `qbank:wallet:read` scope).
  const qbankEntitled = !qbankWallets.isLoading && !qbankWallets.isError;
  // QBank creates wallet rows lazily (only on first credit), so an entitled but
  // uncredited user gets `[]`. Show a zero default coin so a 0 balance is visible.
  const displayWallets: Wallet[] = wallets.some((w) => w.level == null)
    ? wallets
    : [
        ...wallets,
        {
          currencyId: '__default__',
          sub: '',
          name: QBANK_DEFAULT_COIN.name,
          code: QBANK_DEFAULT_COIN.code,
          exchange: null,
          isDefault: true,
          amount: 0,
          defaultCoin: null,
          isEnable: true,
          level: null,
          toNextLevel: null,
        },
      ];

  return (
    <div className="flex flex-col gap-6 px-4 pb-6 pt-5">
      <UserHeaderBanner
        userId={me?.id}
        avatarUrl={me?.avatarUrl}
        avatarFallback={(me?.firstName ?? me?.username ?? '?').charAt(0)}
        onCustomize={() => setCustomizeOpen(true)}
      >
        <div className="min-w-0 pr-12">
          <h1 className="truncate text-base font-bold text-fg drop-shadow">
            {me?.firstName ?? me?.username ?? 'Player'}
          </h1>
          {me?.email && (
            <p className="truncate text-[11px] text-fg-muted drop-shadow">{me.email}</p>
          )}
        </div>
      </UserHeaderBanner>
      <CustomizeSheet
        userId={me?.id}
        open={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
      />

      {/* Social counts chip — three-stat strip linked to the Friends tab. */}
      <Link
        to="/friends"
        className="flex items-center justify-between rounded-xl bg-bg-secondary p-3 transition-opacity active:opacity-80"
      >
        <div className="flex flex-1 items-stretch gap-2">
          <SocialStat label={t('social.tab_friends', { defaultValue: 'Friends' })} count={friendsCount} />
          <span className="w-px bg-border" />
          <SocialStat label={t('social.tab_following', { defaultValue: 'Following' })} count={followingCount} />
          <span className="w-px bg-border" />
          <SocialStat label={t('social.tab_followers', { defaultValue: 'Followers' })} count={followersCount} />
        </div>
        <ChevronRight className="ml-2 h-4 w-4 text-fg-muted" />
      </Link>

      {/* Linked accounts. Clash Royale (qduels) is a real, working link;
          Discord/Telegram are still Wave 2 placeholders. */}
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
          {t('social.linked_accounts', { defaultValue: 'Linked accounts' })}
        </h2>

        <Link
          to="/duels/link"
          className="flex items-center gap-3 rounded-xl bg-bg-secondary p-3 text-left transition-opacity active:opacity-80"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand/15 text-brand">
            <Swords className="h-4 w-4" />
          </span>
          <span className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-fg">
              {t('social.link_clash_royale', {
                defaultValue: 'Clash Royale',
              })}
            </p>
            <p className="truncate text-[11px] text-fg-muted">
              {crLinkedTag
                ? t('social.link_cr_linked', {
                    defaultValue: 'Linked · {{tag}}',
                    tag: crLinkedTag,
                  })
                : crNotLinked || !crLinkQuery.isLoading
                  ? t('social.link_cr_connect', {
                      defaultValue: 'Link to send duels',
                    })
                  : t('social.coming_soon', { defaultValue: 'Coming soon' })}
            </p>
          </span>
          <ChevronRight className="h-4 w-4 text-fg-muted" />
        </Link>

        <button
          type="button"
          disabled
          className="flex items-center gap-3 rounded-xl bg-bg-secondary p-3 text-left opacity-60"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#5865F2]/20 text-[#5865F2]">
            <MessageSquare className="h-4 w-4" />
          </span>
          <span className="flex-1">
            <p className="text-sm font-semibold text-fg">
              {t('social.link_discord', { defaultValue: 'Link Discord' })}
            </p>
            <p className="text-[11px] text-fg-muted">
              {t('social.coming_soon', { defaultValue: 'Coming soon' })}
            </p>
          </span>
        </button>
        <button
          type="button"
          disabled
          className="flex items-center gap-3 rounded-xl bg-bg-secondary p-3 text-left opacity-60"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-sky-500/20 text-sky-400">
            <Send className="h-4 w-4" />
          </span>
          <span className="flex-1">
            <p className="text-sm font-semibold text-fg">
              {t('social.link_telegram', { defaultValue: 'Link Telegram' })}
            </p>
            <p className="text-[11px] text-fg-muted">
              {t('social.coming_soon', { defaultValue: 'Coming soon' })}
            </p>
          </span>
        </button>
      </section>

      {/* Wallet & Levels — render only for entitled users (the read succeeded).
          Non-entitled users (no `qbank:wallet:read` scope → 401) see nothing.
          Entitled-but-uncredited users see a "0 QC" default row. */}
      {env.featureQbankEnabled && qbankEntitled && (
        <section className="flex flex-col gap-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
            {t('qbank.wallet_levels', { defaultValue: 'Wallet & Levels' })}
          </h2>

          <ul className="flex flex-col gap-2">
            {displayWallets.map((w) => (
              <li
                key={w.currencyId}
                className="flex items-center justify-between rounded-xl bg-bg-secondary p-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-fg">{w.name}</p>
                  <p className="text-xs text-fg-muted">{w.code}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-brand">
                    {Number(w.amount ?? 0).toLocaleString()}
                  </p>
                  {w.level != null && (
                    <p className="text-[11px] text-fg-muted">
                      {t('qbank.level', { defaultValue: 'Lv {{n}}', n: w.level })}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
            {t('my_tournaments', { defaultValue: 'My tournaments' })}
          </h2>
          <Link to="/tournaments" className="text-xs text-brand">
            {t('view_all', { defaultValue: 'View all' })}
          </Link>
        </div>

        {myTournaments.isLoading && (
          <div className="flex h-20 items-center justify-center">
            <Loader />
          </div>
        )}

        {!myTournaments.isLoading && !myTournaments.data?.data.length && (
          <div className="rounded-xl bg-bg-secondary p-4 text-center text-sm text-fg-muted">
            {t('my_tournaments_empty', {
              defaultValue: "You haven't joined any tournaments yet.",
            })}
          </div>
        )}

        <ul className="flex flex-col gap-2">
          {myTournaments.data?.data.map((tournament) => (
            <li key={tournament.id}>
              <Link
                to={`/tournaments/${tournament.id}`}
                className="flex items-center justify-between rounded-xl bg-bg-secondary p-3 transition-opacity active:opacity-80"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-fg">{tournament.title}</p>
                  <p className="text-xs text-fg-muted">
                    {formatDateShort(tournament.started_at)}
                  </p>
                </div>
                <StatusBadge status={tournament.status} />
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {env.featureAvatarsEnabled && (
      <section className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
            {t('my_avatars', { defaultValue: 'My avatars' })}
          </h2>
          <Link to="/avatars" className="text-xs text-brand">
            {t('view_all', { defaultValue: 'View all' })}
          </Link>
        </div>

        {myAvatars.isLoading ? (
          <div className="flex h-20 items-center justify-center">
            <Loader />
          </div>
        ) : avatarItems.length === 0 ? (
          <Link
            to="/avatars/new"
            className="flex items-center gap-3 rounded-xl bg-bg-secondary p-4 transition-opacity active:opacity-80"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand/15 text-brand">
              <Sparkles aria-hidden className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-fg">Create your first avatar</p>
              <p className="text-xs text-fg-muted">AI-generated, in seconds.</p>
            </div>
            <ChevronRight className="h-4 w-4 text-fg-muted" />
          </Link>
        ) : (
          <div className="no-scrollbar -mx-4 flex gap-3 overflow-x-auto px-4 pb-1">
            {avatarItems.map((avatar) => (
              <Link
                key={avatar._id}
                to={`/avatars/${avatar._id}`}
                className="flex w-24 shrink-0 flex-col gap-1.5"
              >
                <div className="aspect-square overflow-hidden rounded-xl bg-bg-tertiary">
                  {avatar.thumbnailUrl && (
                    <img
                      src={avatar.thumbnailUrl}
                      alt={avatar.title}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  )}
                </div>
                <p className="truncate text-[11px] text-fg-muted">{avatar.title}</p>
              </Link>
            ))}
          </div>
        )}
      </section>
      )}

      <section className="flex flex-col gap-1">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-fg-muted">
          {t('account', { defaultValue: 'Account' })}
        </h2>

        <ProfileLink to="/chat" icon={MessageCircle}>
          {t('chat', { defaultValue: 'Chat' })}
        </ProfileLink>

        <ProfileLink to="/settings" icon={SettingsIcon}>
          {t('settings', { defaultValue: 'Settings' })}
        </ProfileLink>

        <button
          type="button"
          onClick={signOut}
          className="flex items-center gap-3 rounded-xl bg-bg-secondary p-3 text-left transition-opacity active:opacity-80"
        >
          <LogOut className="h-5 w-5 text-danger" />
          <span className="flex-1 text-sm font-medium text-danger">
            {t('logout', { defaultValue: 'Sign out' })}
          </span>
        </button>
      </section>
    </div>
  );
}

interface ProfileLinkProps {
  to: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  children: React.ReactNode;
}

function SocialStat({ label, count }: { label: string; count: number }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center">
      <p className="text-base font-bold text-fg">{count}</p>
      <p className="text-[10px] uppercase tracking-wide text-fg-muted">{label}</p>
    </div>
  );
}

function ProfileLink({ to, icon: Icon, children }: ProfileLinkProps) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 rounded-xl bg-bg-secondary p-3 transition-opacity active:opacity-80"
    >
      <Icon className="h-5 w-5 text-fg" />
      <span className="flex-1 text-sm font-medium text-fg">{children}</span>
      <ChevronRight className="h-4 w-4 text-fg-muted" />
    </Link>
  );
}
